import { ArtifactsPanel } from "@artifacts/ArtifactsPanel";
import { CapturePanel } from "@capture/CapturePanel";
import { ClinicBrandingPanel } from "@clinic/ClinicBrandingPanel";
import { ClinicSettingsPanel } from "@clinic/ClinicSettingsPanel";
import { CompanionHeader } from "@components/CompanionHeader";
import { ConnectClinicPanel } from "@connector/ConnectClinicPanel";
import { useUser } from "@context/UserContext";
import { PimsActivityPanel } from "@pims/PimsActivityPanel";
import { RecurrencesPanel } from "@recurrences/RecurrencesPanel";
import {
  cn,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@scribenote/brian-design-system";
import { Navigate, useNavigate, useSearch } from "@tanstack/react-router";
import { canAccessAdmin } from "@users/userClient";
import { UsersPanel } from "@users/UsersPanel";
import { ViewsPanel } from "@views/ViewsPanel";
import { useState, type ReactNode } from "react";

const ADMIN_TABS = [
  "connect",
  "settings",
  "pims",
  "recurrences",
  "artifacts",
  "views",
  "users",
  "capture",
] as const;

export type AdminTab = (typeof ADMIN_TABS)[number];

export const isAdminTab = (value: unknown): value is AdminTab =>
  ADMIN_TABS.includes(value as AdminTab);

/** What each divider says, in tab order. */
const TAB_LABELS: Record<AdminTab, string> = {
  connect: "Connect",
  settings: "Settings",
  pims: "PIMS",
  recurrences: "Recurrences",
  artifacts: "Artifacts",
  views: "Views",
  users: "Users",
  capture: "Capture",
};

/**
 * What is filed behind each divider. Thunks, not elements: a panel is only
 * built once its tab has been opened (see `visited`), so the panels nobody
 * asked for never mount and never fetch.
 */
const TAB_PANELS: Record<AdminTab, () => ReactNode> = {
  connect: () => <ConnectClinicPanel />,
  settings: () => (
    <div className="flex flex-col gap-4">
      <ClinicSettingsPanel />
      <ClinicBrandingPanel />
    </div>
  ),
  pims: () => <PimsActivityPanel />,
  recurrences: () => <RecurrencesPanel />,
  artifacts: () => <ArtifactsPanel />,
  views: () => <ViewsPanel />,
  users: () => <UsersPanel />,
  capture: () => <CapturePanel />,
};

/**
 * The admin chart: one tabbed divider per back-office section, each panel a
 * sheet filed behind it. The strip wraps rather than scrolls, so a narrow
 * window staggers the dividers like a fat paper chart instead of pushing
 * controls out of reach.
 */
export const AdminShell = () => {
  const { currentUser } = useUser();
  const { tab } = useSearch({ from: "/admin" });
  const navigate = useNavigate();
  const activeTab = tab ?? "settings";
  // Which panels have been opened at least once. A panel mounts on first open
  // and stays mounted afterwards, so it keeps its state - filters, selections,
  // open replays - across a trip to another divider. Recorded during render (the
  // active tab has been visited by definition): React re-runs this component
  // with the new list before it paints, so the panel still mounts in one commit.
  const [visited, setVisited] = useState<AdminTab[]>([activeTab]);
  if (!visited.includes(activeTab)) setVisited([...visited, activeTab]);

  if (!currentUser) return null;
  if (!canAccessAdmin(currentUser)) return <Navigate to="/" />;

  return (
    <div className="flex h-screen w-screen flex-col bg-paper font-body text-ink-body">
      <CompanionHeader back={{ title: "Admin", testId: "admin-back" }} />

      <main
        aria-label="Admin panel"
        className="flex min-w-0 flex-1 flex-col overflow-hidden p-2"
      >
        <Tabs
          value={activeTab}
          onValueChange={(value: string) =>
            navigate({ to: "/admin", search: { tab: value as AdminTab } })
          }
          data-testid="admin-panel-tabs"
          data-intercom-target="admin-panel-tabs"
          className="min-h-0 min-w-0 flex-1"
        >
          <TabList aria-label="Admin sections" className="flex-wrap px-1">
            {ADMIN_TABS.map((value) => (
              <Tab
                key={value}
                value={value}
                data-testid={`admin-panel-tabs-tab-${value}`}
                data-intercom-target={`admin-panel-tabs-tab-${value}`}
              >
                {TAB_LABELS[value]}
              </Tab>
            ))}
          </TabList>

          {ADMIN_TABS.map((value) => (
            <TabPanel
              key={value}
              value={value}
              data-testid={`admin-panel-tabs-panel-${value}`}
              data-intercom-target={`admin-panel-tabs-panel-${value}`}
              className={cn(
                "min-h-0",
                value === activeTab && "flex flex-1 flex-col",
              )}
            >
              {/* Scrolling lives inside the sheet so the scrollbar runs
                  within the panel's rules, not across its edge. */}
              <div className="min-h-0 flex-1 overflow-auto">
                {visited.includes(value) ? TAB_PANELS[value]() : null}
              </div>
            </TabPanel>
          ))}
        </Tabs>
      </main>
    </div>
  );
};
